/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Color Harmony Utilities
 *
 * Implements color harmony theory to generate harmonious color palettes
 * from a primary color. Supports complementary, analogous, triadic, and
 * split-complementary color schemes.
 *
 * Based on color theory principles:
 * - Complementary: 180° hue shift (high contrast)
 * - Analogous: 30-60° hue shift (harmonious)
 * - Triadic: 120° hue shift (balanced)
 * - Split-Complementary: Base + two adjacent to complement
 */

import { hexToHsl, parseHsl, type ColorHsl } from "./color-utils";

export type HarmonyType =
  | "complementary"
  | "analogous"
  | "triadic"
  | "split-complementary";

export interface HarmonyOptions {
  /**
   * Type of color harmony to generate
   * @default "complementary"
   */
  type?: HarmonyType;
  /**
   * Saturation adjustment for secondary colors (-100 to 100)
   * @default 0
   */
  saturationAdjust?: number;
  /**
   * Lightness adjustment for secondary colors (-100 to 100)
   * @default 0
   */
  lightnessAdjust?: number;
  /**
   * Whether to maintain similar saturation to primary
   * @default true
   */
  maintainSaturation?: boolean;
  /**
   * Whether to maintain similar lightness to primary
   * @default true
   */
  maintainLightness?: boolean;
}

/**
 * Generate a complementary color (180° hue shift)
 * High contrast, vibrant - best for CTAs and emphasis
 */
export function generateComplementaryColor(
  primaryColor: string | ColorHsl,
  options: Omit<HarmonyOptions, "type"> = {},
): string {
  const primary =
    typeof primaryColor === "string"
      ? parseHsl(hexToHsl(primaryColor))
      : primaryColor;
  const {
    saturationAdjust = 0,
    lightnessAdjust = 0,
    maintainSaturation = true,
    maintainLightness = true,
  } = options;

  const complementaryHue = (primary.h + 180) % 360;
  const saturation = maintainSaturation
    ? Math.max(0, Math.min(100, primary.s + saturationAdjust))
    : primary.s;
  const lightness = maintainLightness
    ? Math.max(0, Math.min(100, primary.l + lightnessAdjust))
    : primary.l;

  return `${Math.round(complementaryHue)} ${Math.round(saturation)}% ${Math.round(lightness)}%`;
}

/**
 * Generate an analogous color (30-60° hue shift)
 * Harmonious, cohesive - best for gradients and backgrounds
 */
export function generateAnalogousColor(
  primaryColor: string | ColorHsl,
  options: {
    /**
     * Hue shift direction: "warm" (toward red/yellow) or "cool" (toward blue/green)
     * @default "warm"
     */
    direction?: "warm" | "cool";
    /**
     * Hue shift amount in degrees (30-60 recommended)
     * @default 30
     */
    shift?: number;
    saturationAdjust?: number;
    lightnessAdjust?: number;
  } = {},
): string {
  const primary =
    typeof primaryColor === "string"
      ? parseHsl(hexToHsl(primaryColor))
      : primaryColor;
  const {
    direction = "warm",
    shift = 30,
    saturationAdjust = -10, // Slightly desaturate for harmony
    lightnessAdjust = 0,
  } = options;

  const shiftAmount = direction === "warm" ? shift : -shift;
  const analogousHue = (primary.h + shiftAmount + 360) % 360;
  const saturation = Math.max(0, Math.min(100, primary.s + saturationAdjust));
  const lightness = Math.max(0, Math.min(100, primary.l + lightnessAdjust));

  return `${Math.round(analogousHue)} ${Math.round(saturation)}% ${Math.round(lightness)}%`;
}

/**
 * Generate a triadic color (120° hue shift)
 * Balanced, dynamic - best for multi-color schemes
 */
export function generateTriadicColor(
  primaryColor: string | ColorHsl,
  options: {
    /**
     * Which triadic color to generate: "first" (120°) or "second" (240°)
     * @default "first"
     */
    position?: "first" | "second";
    saturationAdjust?: number;
    lightnessAdjust?: number;
  } = {},
): string {
  const primary =
    typeof primaryColor === "string"
      ? parseHsl(hexToHsl(primaryColor))
      : primaryColor;
  const {
    position = "first",
    saturationAdjust = 0,
    lightnessAdjust = 0,
  } = options;

  const shift = position === "first" ? 120 : 240;
  const triadicHue = (primary.h + shift) % 360;
  const saturation = Math.max(0, Math.min(100, primary.s + saturationAdjust));
  const lightness = Math.max(0, Math.min(100, primary.l + lightnessAdjust));

  return `${Math.round(triadicHue)} ${Math.round(saturation)}% ${Math.round(lightness)}%`;
}

/**
 * Generate split-complementary colors
 * Base color + two colors adjacent to the complement
 * Balanced contrast - best for complex interfaces
 */
export function generateSplitComplementaryColors(
  primaryColor: string | ColorHsl,
  options: {
    /**
     * Split angle from complement (typically 30-45°)
     * @default 30
     */
    splitAngle?: number;
    saturationAdjust?: number;
    lightnessAdjust?: number;
  } = {},
): { first: string; second: string } {
  const primary =
    typeof primaryColor === "string"
      ? parseHsl(hexToHsl(primaryColor))
      : primaryColor;
  const {
    splitAngle = 30,
    saturationAdjust = -5,
    lightnessAdjust = 0,
  } = options;

  const complementHue = (primary.h + 180) % 360;
  const firstHue = (complementHue - splitAngle + 360) % 360;
  const secondHue = (complementHue + splitAngle) % 360;

  const saturation = Math.max(0, Math.min(100, primary.s + saturationAdjust));
  const lightness = Math.max(0, Math.min(100, primary.l + lightnessAdjust));

  return {
    first: `${Math.round(firstHue)} ${Math.round(saturation)}% ${Math.round(lightness)}%`,
    second: `${Math.round(secondHue)} ${Math.round(saturation)}% ${Math.round(lightness)}%`,
  };
}

/**
 * Generate a harmonious color palette from a primary color
 * Returns primary, secondary, and optional accent colors
 */
export function generateHarmoniousPalette(
  primaryColor: string | ColorHsl,
  options: HarmonyOptions = {},
): {
  primary: string;
  secondary: string;
  accent?: string;
  tertiary?: string;
} {
  const primary =
    typeof primaryColor === "string"
      ? parseHsl(hexToHsl(primaryColor))
      : primaryColor;
  const primaryHsl = `${primary.h} ${primary.s}% ${primary.l}%`;
  const { type = "complementary", ...harmonyOptions } = options;

  let secondary: string;
  let accent: string | undefined;
  let tertiary: string | undefined;

  switch (type) {
    case "complementary":
      secondary = generateComplementaryColor(primary, harmonyOptions);
      break;

    case "analogous":
      secondary = generateAnalogousColor(primary, {
        direction: "warm",
        shift: 30,
        ...harmonyOptions,
      });
      accent = generateAnalogousColor(primary, {
        direction: "cool",
        shift: 30,
        ...harmonyOptions,
      });
      break;

    case "triadic":
      secondary = generateTriadicColor(primary, {
        position: "first",
        ...harmonyOptions,
      });
      tertiary = generateTriadicColor(primary, {
        position: "second",
        ...harmonyOptions,
      });
      break;

    case "split-complementary":
      const split = generateSplitComplementaryColors(primary, {
        splitAngle: 30,
        ...harmonyOptions,
      });
      secondary = split.first;
      accent = split.second;
      break;

    default:
      secondary = generateComplementaryColor(primary, harmonyOptions);
  }

  return {
    primary: primaryHsl,
    secondary,
    ...(accent && { accent }),
    ...(tertiary && { tertiary }),
  };
}

/**
 * Determine the best harmony type for a given primary color
 * Based on hue position and saturation
 */
export function recommendHarmonyType(
  primaryColor: string | ColorHsl,
): HarmonyType {
  const primary =
    typeof primaryColor === "string"
      ? parseHsl(hexToHsl(primaryColor))
      : primaryColor;

  // High saturation colors work well with complementary
  if (primary.s > 70) {
    return "complementary";
  }

  // Medium saturation: triadic for balance
  if (primary.s > 40) {
    return "triadic";
  }

  // Low saturation: analogous for harmony
  return "analogous";
}
